// ═══════════════════════════════════════════════════════════════
// VaultZkUnlockModal.js — Modale de déverrouillage V2 (Session G)
// ═══════════════════════════════════════════════════════════════
//
// Bloque l'accès au coffre tant que le user n'a pas saisi sa
// passphrase (et son code TOTP si 2FA activé). Sur succès, le DEK
// est posé dans le VaultZkContext (mémoire navigateur uniquement).
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { Lock, Shield, Eye, EyeOff, KeyRound, RotateCcw, Loader2 } from 'lucide-react';
import {
    deriveKEKFromPassphrase, unwrapDEK, verifyTestBlob,
    deriveKEKFromMnemonic, validateMnemonic24,
} from '../../utils/vaultZkCrypto';
import { unwrapTotpSecret, verifyTotpCode } from '../../utils/vaultZkTotp';
import { confirmUnlock, recoverZk } from '../../services/vaultZkApi';
import { useVaultZk } from './VaultZkContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 8, 18, 0.78)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: (isDark) => ({
        width: 'min(440px, 92vw)',
        background: isDark ? '#13131a' : '#ffffff',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        borderRadius: 16,
        padding: '28px 28px 24px',
        boxShadow: '0 24px 48px rgba(76, 29, 149, 0.35)',
        border: isDark ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.18)',
    }),
    title: { fontSize: 17, fontWeight: 600, margin: '8px 0 4px' },
    subtitle: (isDark) => ({
        fontSize: 13, color: isDark ? '#9090a8' : '#6a6a85',
        margin: '0 0 18px', lineHeight: 1.5,
    }),
    label: { fontSize: 12, fontWeight: 500, marginBottom: 6, display: 'block' },
    input: (isDark) => ({
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px', fontSize: 14,
        background: isDark ? '#1c1c28' : '#f7f7fb',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 8, outline: 'none',
        fontFamily: 'inherit',
    }),
    inputGroup: { position: 'relative', marginBottom: 14 },
    eyeBtn: (isDark) => ({
        position: 'absolute', right: 10, top: 9,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: isDark ? '#9090a8' : '#6a6a85', padding: 4,
    }),
    err: { color: '#ef4444', fontSize: 12.5, marginTop: 6, marginBottom: 10 },
    btnPrimary: (busy) => ({
        width: '100%', padding: '11px 14px',
        background: busy ? '#6d28d9' : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        color: '#fff', border: 'none', borderRadius: 9,
        fontSize: 14, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: '0 6px 18px rgba(109, 40, 217, 0.32)',
    }),
    secondaryRow: {
        display: 'flex', justifyContent: 'space-between',
        marginTop: 14, fontSize: 12.5,
    },
    link: { background: 'transparent', border: 'none', color: '#8b5cf6',
        cursor: 'pointer', padding: 0, fontSize: 12.5, fontWeight: 500 },
};

export default function VaultZkUnlockModal({ challenge, onCancel }) {
    const { unlock } = useVaultZk();
    const { isDark } = useTheme();
    const [view, setView] = useState('passphrase'); // 'passphrase' | 'recovery'
    const [passphrase, setPassphrase] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [totpCode, setTotpCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Recovery view state
    const [mnemonicRaw, setMnemonicRaw] = useState('');
    const [newPwd1, setNewPwd1] = useState('');
    const [newPwd2, setNewPwd2] = useState('');

    const lockedUntil = challenge?.vault_locked_until && new Date(challenge.vault_locked_until) > new Date()
        ? new Date(challenge.vault_locked_until) : null;
    const burned = !!challenge?.vault_burned_at;

    async function tryUnlock() {
        if (busy || lockedUntil || burned) return;
        if (!passphrase) { setErr('Saisissez votre passphrase.'); return; }
        setErr(''); setBusy(true);
        try {
            const kek = await deriveKEKFromPassphrase(passphrase, challenge.salt, challenge.iter);
            const ok = await verifyTestBlob(challenge.test_blob, kek);
            if (!ok) {
                await confirmUnlock(false);
                setErr('Passphrase incorrecte.');
                return;
            }
            const dek = await unwrapDEK(challenge.wrapped_dek, kek);
            if (!dek) {
                await confirmUnlock(false);
                setErr('Échec du déverrouillage de la clé maître.');
                return;
            }
            // 2FA TOTP si activé
            if (challenge.totp_enabled) {
                if (!/^\d{6}$/.test(totpCode)) {
                    setErr('Saisissez le code TOTP à 6 chiffres.');
                    return;
                }
                const secret = await unwrapTotpSecret(challenge.totp_secret_blob, kek);
                if (!secret) {
                    await confirmUnlock(false, { totp_used: true });
                    setErr('Secret 2FA illisible — passphrase erronée pour le secret.');
                    return;
                }
                const totpOk = await verifyTotpCode(secret, totpCode);
                if (!totpOk) {
                    await confirmUnlock(false, { totp_used: true });
                    setErr('Code 2FA incorrect ou expiré.');
                    return;
                }
            }
            await confirmUnlock(true, { totp_used: !!challenge.totp_enabled });
            unlock(dek);
            toast.success('Coffre-fort déverrouillé');
        } catch (e) {
            setErr(`Erreur de déverrouillage : ${e.message}`);
        } finally {
            setBusy(false);
        }
    }

    async function tryRecovery() {
        if (busy) return;
        const words = mnemonicRaw.toLowerCase().trim().split(/\s+/).filter(Boolean);
        if (words.length !== 24) { setErr('Le mnémonique doit contenir exactement 24 mots.'); return; }
        const isValid = await validateMnemonic24(words);
        if (!isValid) { setErr('Mnémonique invalide (mot inconnu ou checksum incorrect).'); return; }
        if (newPwd1.length < 12) { setErr('Nouvelle passphrase : au moins 12 caractères.'); return; }
        if (newPwd1 !== newPwd2) { setErr('Les passphrases ne correspondent pas.'); return; }
        setErr(''); setBusy(true);
        try {
            const recoveryKek = await deriveKEKFromMnemonic(words, challenge.recovery_salt, challenge.iter);
            const dek = await unwrapDEK(challenge.recovery_blob, recoveryKek);
            if (!dek) {
                setErr('Mnémonique ne correspond pas à ce compte.');
                return;
            }
            // Re-wrap DEK avec nouvelle passphrase
            const { randomSaltBase64 } = await import('../../utils/vaultZkCrypto');
            const newSalt = randomSaltBase64(32);
            const newKek = await deriveKEKFromPassphrase(newPwd1, newSalt, challenge.iter);
            const { wrapDEK, createTestBlob, VAULT_ZK_KDF_LABEL } = await import('../../utils/vaultZkCrypto');
            const newWrapped = await wrapDEK(dek, newKek);
            const newTest = await createTestBlob(newKek);

            await recoverZk({
                kdf: VAULT_ZK_KDF_LABEL,
                iter: challenge.iter,
                salt: newSalt,
                wrapped_dek: newWrapped,
                test_blob: newTest,
            });
            unlock(dek);
            toast.success('Récupération réussie — passphrase remplacée');
        } catch (e) {
            setErr(`Erreur de récupération : ${e.message}`);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Déverrouillage du coffre-fort">
            <div style={styles.modal(isDark)} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Shield size={20} color="#8b5cf6" />
                    <h2 style={styles.title}>
                        {view === 'recovery' ? 'Récupération via mnémonique' : 'Coffre-fort verrouillé'}
                    </h2>
                </div>
                <p style={styles.subtitle(isDark)}>
                    {view === 'recovery'
                        ? 'Saisissez vos 24 mots de récupération puis choisissez une nouvelle passphrase.'
                        : 'Saisissez votre passphrase pour déchiffrer vos items dans ce navigateur. Elle ne quitte jamais cet onglet.'}
                </p>

                {burned && (
                    <div style={{ ...styles.err, marginBottom: 16 }}>
                        Coffre-fort auto-détruit (trop de tentatives). Contactez l'administrateur.
                    </div>
                )}

                {lockedUntil && !burned && (
                    <div style={{ ...styles.err, marginBottom: 16 }}>
                        Déverrouillage temporairement bloqué jusqu'au {lockedUntil.toLocaleString('fr-FR')}.
                    </div>
                )}

                {!burned && !lockedUntil && view === 'passphrase' && (
                    <>
                        <label style={styles.label}>Passphrase</label>
                        <div style={styles.inputGroup}>
                            <input
                                type={showPwd ? 'text' : 'password'}
                                value={passphrase}
                                onChange={(e) => setPassphrase(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') tryUnlock(); }}
                                autoFocus
                                style={{ ...styles.input(isDark), paddingRight: 38 }}
                                placeholder="Votre passphrase"
                            />
                            <button type="button" style={styles.eyeBtn(isDark)} onClick={() => setShowPwd(!showPwd)}>
                                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>

                        {challenge?.totp_enabled && (
                            <>
                                <label style={styles.label}>Code 2FA (Authenticator)</label>
                                <input
                                    type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') tryUnlock(); }}
                                    placeholder="123456"
                                    style={{ ...styles.input(isDark), letterSpacing: 4, fontSize: 18, textAlign: 'center', marginBottom: 14 }}
                                />
                            </>
                        )}

                        {err && <div style={styles.err}>{err}</div>}

                        <button style={styles.btnPrimary(busy)} disabled={busy} onClick={tryUnlock}>
                            {busy ? <Loader2 size={16} className="spin" /> : <Lock size={16} />}
                            Déverrouiller
                        </button>

                        <div style={styles.secondaryRow}>
                            <button type="button" style={styles.link} onClick={() => { setErr(''); setView('recovery'); }}>
                                <KeyRound size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                                J'ai oublié → mnémonique
                            </button>
                            {onCancel && (
                                <button type="button" style={styles.link} onClick={onCancel}>Plus tard</button>
                            )}
                        </div>
                    </>
                )}

                {view === 'recovery' && (
                    <>
                        <label style={styles.label}>Vos 24 mots BIP39 (séparés par espaces)</label>
                        <textarea
                            value={mnemonicRaw}
                            onChange={(e) => setMnemonicRaw(e.target.value)}
                            placeholder="abandon ability able about above..."
                            rows={4}
                            style={{ ...styles.input(isDark), resize: 'vertical', marginBottom: 12, fontFamily: 'monospace', fontSize: 13 }}
                        />
                        <label style={styles.label}>Nouvelle passphrase (min. 12 caractères)</label>
                        <input
                            type="password" value={newPwd1}
                            onChange={(e) => setNewPwd1(e.target.value)}
                            style={{ ...styles.input(isDark), marginBottom: 10 }}
                        />
                        <label style={styles.label}>Confirmer la nouvelle passphrase</label>
                        <input
                            type="password" value={newPwd2}
                            onChange={(e) => setNewPwd2(e.target.value)}
                            style={{ ...styles.input(isDark), marginBottom: 14 }}
                        />

                        {err && <div style={styles.err}>{err}</div>}

                        <button style={styles.btnPrimary(busy)} disabled={busy} onClick={tryRecovery}>
                            {busy ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />}
                            Récupérer & remplacer la passphrase
                        </button>

                        <div style={{ ...styles.secondaryRow, justifyContent: 'flex-start' }}>
                            <button type="button" style={styles.link} onClick={() => { setErr(''); setView('passphrase'); }}>
                                ← Retour passphrase
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
